import React, { useEffect, useRef } from "react";
import classnames from "classnames";
import SVG from "react-inlinesvg";
import Tip from "../../../common/Tip/index.js";
import { createMenuInteractionEvent } from "src/events/MenuInteractionEvent";
import { Link, useLocation } from "react-router-dom";
import { useAppStore } from "src/stores/appStore";
import usePopoverClose from "../../../../hooks/usePopoverClose";
import StickyNoteAnchor from "../../../StickyTour/StickyNoteAnchor";
import { AnchorIds, TourIds, TourSignal } from "../../../StickyTour/TourTypes";
import { useStickyTourStore } from "src/stores/stickyTourStore";
import { useOnboardingStore } from "src/stores/onboardingStore";

const MoreMenu = ({
  MoreMenuDefaultItemsMap,
  labsOpen,
  setLabsOpen,
  setToneOpen,
  setStoryToolOpen,
  setDescribeOpen,
  setWriteOpen,
  userData,
  subscription,
  width,
  hideBreakpointLabs,
  labItems,
  triggerProps,
  trashedItem,
  openCustomizePluginsModal,
  pluginSchemasMap,
  makePluginHandler,
  disabled = false,
}) => {
  const user = useAppStore((state) => state.user);
  const location = useLocation();
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);
  const activeTourId = useStickyTourStore((state) => state.activeTourId);
  const generatedPlugin = useOnboardingStore((state) => state.generatedPlugin);
  const triggerRef = useRef(null);

  const { containerRef, handleBlur } = usePopoverClose(labsOpen, () => setLabsOpen(false), {
    onEscapeFocus: () => triggerRef.current?.focus(),
  });

  // Focus first menu item when dropdown opens
  useEffect(() => {
    if (labsOpen && containerRef.current) {
      requestAnimationFrame(() => {
        const firstItem = containerRef.current.querySelector('.secondMenu a, .secondMenu button');
        if (firstItem) firstItem.focus();
      });
    }
  }, [labsOpen, containerRef]);

  const isGeneratedPlugin = (plugin) => {
    if (activeTourId !== TourIds.PLUGINS_QUICKSTART || !plugin) return false;
    if (!generatedPlugin) return showPluginMatchFallback(plugin);

    return (
      plugin.shortId === generatedPlugin.shortId ||
      plugin.slug === generatedPlugin.slug ||
      plugin.name === generatedPlugin.name
    );
  };

  const showPluginMatchFallback = (plugin) => {
    return Boolean(plugin?.blocks?.some((block) => block.blockType === "input_document"));
  };

  return (
    <li
      ref={containerRef}
      className={classnames("wormhole dropdown labs", { disabled: disabled || !!trashedItem })}
      aria-disabled={disabled || !!trashedItem}
      onBlur={handleBlur}
    >
      <div
        ref={triggerProps.ref}
        className={classnames({ wrapper: true, "more-menu-open": labsOpen })}
      >
        {userData?.data.showPluginTip && (
          <Tip
            dismiss={() => {
              userData?.update({ showPluginTip: false });
            }}
          >
            Click here to use the Plugin you added!
          </Tip>
        )}
        <StickyNoteAnchor anchorId={AnchorIds.MORE_TOOLS_BUTTON} />
        <button
          type="button"
          ref={triggerRef}
          className="more-tools-trigger use-component-styles"
          onClick={(e) => {
            if (disabled) {
              return;
            }

            e.stopPropagation();

            if (labsOpen) {
              setLabsOpen(false);
            } else {
              setToneOpen(false);
              setStoryToolOpen(false);
              setDescribeOpen(false);
              setWriteOpen(false);
              setLabsOpen(true);
              sendFlowSignal(TourSignal.MORE_TOOLS_CLICKED);
            }

            userData?.update({ showPluginTip: false });

            const menuInteractionEvent = createMenuInteractionEvent();
            e.currentTarget.dispatchEvent(menuInteractionEvent);
          }}
          aria-label="More Tools"
          aria-expanded={labsOpen}
        >
          <div className="plugin-lockup">
            {subscription.hasAccessToPlugins && (
              <>
                <SVG
                  aria-hidden="true"
                  src={width > hideBreakpointLabs || labsOpen ? "/assets/plugin.svg" : "/assets/hotdog-menu.svg"}
                />
                {(width > hideBreakpointLabs || labsOpen) && (
                  <span className="label">
                    More Tools
                  </span>
                )}
              </>
            )}
            {!subscription.hasAccessToPlugins && (
              <span className="label">
                More
              </span>
            )}
          </div>
          {width > hideBreakpointLabs && (
            <div className="arrow">
              <SVG
                aria-hidden="true"
                src="/assets/down-chevron.svg"
              />
            </div>
          )}
        </button>

        {labsOpen && (
          <ul className="secondMenu" role="menu" onClick={() => { setLabsOpen(false); sendFlowSignal(TourSignal.PLUGIN_CLICKED); }}>
            <li>
              {/* if we still need to add a hasAccessToPlugins check here, we can do it here */}
              <Link
                className="plugins-link"
                to={{ pathname: `/app/plugins`, state: { from: location.pathname } }}
              >
                Explore Plugins {`->`}
              </Link>
            </li>
            {user && (
              <li>
                <Link
                  className="plugins-link"
                  to={{ pathname: `/app/my-plugin/new`, state: { from: location.pathname } }}
                >
                  Create Plugin {`->`}
                </Link>
              </li>
            )}
            <li>
              <button
                type="button"
                className="plugins-link use-component-styles"
                onClick={openCustomizePluginsModal}
              >
                Reorder this Menu…
              </button>
            </li>
            {labItems.map((item, i) => {
              const anchorIndex = labItems.length > 1 ? 1 : 0;
              const isAnchorItem = i === anchorIndex;
              // check if it's a default item
              if (MoreMenuDefaultItemsMap[item]) {
                const shouldAnchorPluginFallback =
                  activeTourId === TourIds.PLUGINS_QUICKSTART &&
                  !generatedPlugin &&
                  item === "brainstorm";

                return (
                  <li key={i} style={{ position: "relative" }}>
                    {isAnchorItem && <StickyNoteAnchor anchorId={AnchorIds.MORE_TOOLS_DROPDOWN} />}
                    {shouldAnchorPluginFallback && <StickyNoteAnchor anchorId={AnchorIds.CUSTOM_PLUGIN_BUTTON} />}
                    {MoreMenuDefaultItemsMap[item].customMoreMenuComponent || ( // if no custom component, render default:
                      <button
                        type="button"
                        data-testid={shouldAnchorPluginFallback ? "plugin-tour-custom-plugin-button" : undefined}
                        className="use-component-styles"
                        onClick={MoreMenuDefaultItemsMap[item].handler}
                      >
                        {MoreMenuDefaultItemsMap[item].name}
                      </button>
                    )}
                  </li>
                );
              }
              // otherwise it's a custom plugin
              const plugin = pluginSchemasMap[item];
              const handler = makePluginHandler(item, pluginSchemasMap);
              if (!plugin || !handler) return null;
              const shouldAnchorPlugin = isGeneratedPlugin(plugin.plugin);
              return (
                <li key={i} style={{ position: "relative" }}>
                  {isAnchorItem && <StickyNoteAnchor anchorId={AnchorIds.MORE_TOOLS_DROPDOWN} />}
                  <button
                    type="button"
                    data-testid={shouldAnchorPlugin ? "plugin-tour-custom-plugin-button" : undefined}
                    className="use-component-styles"
                    onClick={() => {
                      setLabsOpen(false);
                      handler();
                    }}
                  >
                    {shouldAnchorPlugin && <StickyNoteAnchor anchorId={AnchorIds.CUSTOM_PLUGIN_BUTTON} />}
                    {plugin.plugin.name}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </li>
  );
};

export default MoreMenu;
